import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  streamOutput,
  type OutputStreamEvent,
  type OutputFormat,
  type TaskType,
  type PlatformSkill,
  type PlatformOutputMode,
  type PresentationAspect,
  type PresentationPayload,
  type LinkedinPostPayload,
  type XPostPayload,
  type NewsletterPayload,
} from './output-api'
import { vaultKeys } from '@/features/vault/use-vault'
import {
  abortRun,
  beginRun,
  clearRun,
  isRunController,
  setRunAbortController,
  updateRunState,
  useRunStore,
} from '@/features/runs/run-store'

export interface OutputState {
  status: string
  detail?: string
  taskType?: TaskType
  platform?: PlatformSkill
  mode?: PlatformOutputMode
  presentationFormat?: PresentationAspect
  format?: OutputFormat
  model?: string
  streamingContent: string
  result?: { vault_id: string; vault_path: string; storage_url?: string; task_type: TaskType }
  jsonPayload?: LinkedinPostPayload | XPostPayload | NewsletterPayload | PresentationPayload | Record<string, unknown>
  error?: string
  isRunning: boolean
}

export function useOutput() {
  const qc = useQueryClient()
  // Globaler Store statt lokalem State (M2-B Run-Survival).
  const slice = useRunStore((s) => s.output)

  const start = useCallback(
    async (opts: {
      platform: PlatformSkill
      dossier: string
      auftrag: string
      mode?: PlatformOutputMode
      topicFreshness?: boolean
      presentationFormat?: PresentationAspect
      useWebsiteStyleOverride?: boolean
      websiteStyleUrl?: string
      brand?: string
      audience?: string
      length?: string
      sessionId?: string
      primaryBrandId?: string | null
      secondaryBrandIds?: string[]
    }) => {
      abortRun('output')
      const controller = new AbortController()
      setRunAbortController('output', controller)

      beginRun(
        'output',
        {
          status: 'queued',
          streamingContent: '',
          isRunning: true,
        },
        opts.sessionId ?? null,
      )

      try {
        await streamOutput({
          ...opts,
          signal: controller.signal,
          onEvent: (event: OutputStreamEvent) => {
            if (!isRunController('output', controller)) return
            updateRunState('output', (prev) => {
              switch (event.type) {
                case 'status':
                  return { ...prev, status: event.phase, detail: event.detail }
                case 'classified':
                  return {
                    ...prev,
                    taskType: event.task_type,
                    platform: event.platform,
                    mode: event.mode,
                    format: event.format,
                    model: event.model,
                  }
                case 'token':
                  return { ...prev, streamingContent: prev.streamingContent + event.content }
                case 'done':
                  return {
                    ...prev,
                    status: 'done',
                    isRunning: false,
                    result: {
                      vault_id: event.vault_id,
                      vault_path: event.vault_path,
                      storage_url: event.storage_url,
                      task_type: event.task_type,
                    },
                    jsonPayload: event.json_payload,
                  }
                case 'error':
                  return { ...prev, status: 'error', isRunning: false, error: event.message }
                default:
                  return prev
              }
            })
            if (event.type === 'done') {
              void qc.invalidateQueries({ queryKey: vaultKeys.files() })
              toast.success(`Output erstellt: ${event.vault_path}`)
            }
            if (event.type === 'error') toast.error(event.message)
          },
        })
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Output fehlgeschlagen'
        toast.error(message)
        updateRunState('output', (prev) => ({ ...prev, status: 'error', isRunning: false, error: message }))
      }
    },
    [qc],
  )

  const reset = useCallback(() => {
    abortRun('output')
    clearRun('output')
  }, [])

  return { start, reset, state: slice.state, sessionId: slice.sessionId }
}
